import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import DashboardLayout from '../../common/DashboardLayout';
import StateAdminServices from '../../../services/StateAdminServices';
import './StateAdminDashboard.css';

// State code to name mapping
const STATE_NAMES = {
  'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
  'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
  'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
  'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
  'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
  'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
  'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
  'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
  'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
  'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
  'DC': 'District of Columbia'
};

const StateAdminDashboard = () => {
  const { userrole } = useContext(AuthContext);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const stateId = localStorage.getItem('stateid') || user.stateid || 'IN';
  const stateName = STATE_NAMES[stateId] || stateId;

  const [activeTab, setActiveTab] = useState('overview');
  const [schools, setSchools] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    loadStateData();
  }, []);

  const loadStateData = async () => {
    setIsLoading(true);
    try {
      const StateAdminServices = (await import('../../../services/StateAdminServices')).default;
      const response = await StateAdminServices.getStateData();
      if (response.success && response.data) {
        setSchools(response.data.schools || []);
        setMetrics(response.data.metrics || null);
      } else {
        // Fallback to empty data if API fails
        setSchools([]);
        setMetrics({
          totalSchools: 0,
          totalTeachers: 0,
          totalCertifiedTeachers: 0,
          overallComplianceRate: 0,
          certificatesIssuedThisMonth: 0,
          certificatesIssuedThisYear: 0,
          atRiskSchools: 0,
          nonCompliantSchools: 0
        });
      }
    } catch (error) {
      console.error('Error loading state data:', error);
      // Set empty data on error
      setSchools([]);
      setMetrics({
        totalSchools: 0,
        totalTeachers: 0,
        totalCertifiedTeachers: 0,
        overallComplianceRate: 0,
        certificatesIssuedThisMonth: 0,
        certificatesIssuedThisYear: 0,
        atRiskSchools: 0,
        nonCompliantSchools: 0
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getFilteredSchools = () => {
    let filtered = schools;

    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(s => s.complianceStatus === filterStatus);
    }

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(s =>
        s.schoolName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.district?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.schoolName || '').localeCompare(b.schoolName || '');
        case 'compliance':
          return (b.compliancePercentage || 0) - (a.compliancePercentage || 0);
        case 'teachers':
          return (b.totalTeachers || 0) - (a.totalTeachers || 0);
        case 'activity':
          return new Date(b.lastActivityDate || 0).getTime() - new Date(a.lastActivityDate || 0).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'compliant':
        return 'status-badge status-compliant';
      case 'at-risk':
        return 'status-badge status-at-risk';
      case 'non-compliant':
        return 'status-badge status-non-compliant';
      default:
        return 'status-badge';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const exportToCSV = () => {
    const filteredSchools = getFilteredSchools();
    const headers = ['School Name', 'Type', 'City', 'District', 'Total Teachers', 'Certified Teachers', 'Compliance %', 'Status', 'Last Activity'];
    const rows = filteredSchools.map(s => [
      s.schoolName || '',
      s.schoolType || '',
      s.city || '',
      s.district || 'N/A',
      s.totalTeachers || 0,
      s.certifiedTeachers || 0,
      s.compliancePercentage || 0,
      s.complianceStatus || '',
      formatDate(s.lastActivityDate)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${stateId.toLowerCase()}-schools-compliance-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getCityStats = () => {
    const cityMap = new Map();

    schools.forEach(school => {
      const city = school.city || 'Unknown';
      const existing = cityMap.get(city) || { total: 0, compliant: 0 };
      cityMap.set(city, {
        total: existing.total + 1,
        compliant: existing.compliant + (school.compliancePercentage || 0)
      });
    });

    return Array.from(cityMap.entries())
      .map(([name, data]) => ({
        name,
        avgCompliance: Math.round(data.compliant / data.total)
      }))
      .sort((a, b) => b.avgCompliance - a.avgCompliance);
  };

  const getSchoolTypeStats = () => {
    const typeMap = new Map();

    schools.forEach(school => {
      const type = school.schoolType || 'Unknown';
      typeMap.set(type, (typeMap.get(type) || 0) + 1);
    });

    return Array.from(typeMap.entries())
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  };

  if (isLoading) {
    return (
      <DashboardLayout role={4} activeMenu="State Dashboard" layoutType="topnav">
        <div className="state-admin-dashboard">
          <div className="loading-spinner">Loading {stateName} state data...</div>
        </div>
      </DashboardLayout>
    );
  }

  const filteredSchools = getFilteredSchools();

  return (
    <DashboardLayout role={4} activeMenu="State Dashboard" layoutType="topnav">
      <div className="state-admin-dashboard">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1>{stateName} State Education Dashboard</h1>
            <p className="header-subtitle">
              Welcome, {user.name || user.email} | Active Shooter Safety Training Compliance
            </p>
          </div>
          <button className="btn-export" onClick={exportToCSV}>
            📊 Export Report
          </button>
        </div>

        {/* Tabs */}
        <div className="dashboard-tabs">
          <button
            className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            📊 Overview
          </button>
          <button
            className={`tab ${activeTab === 'schools' ? 'active' : ''}`}
            onClick={() => setActiveTab('schools')}
          >
            🏫 Schools ({schools.length})
          </button>
          <button
            className={`tab ${activeTab === 'compliance' ? 'active' : ''}`}
            onClick={() => setActiveTab('compliance')}
          >
            ✅ Compliance
          </button>
          <button
            className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            📈 Analytics
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && metrics && (
          <div className="tab-content">
            <div className="metrics-grid">
              <div className="metric-card">
                <div className="metric-icon">🏫</div>
                <div className="metric-content">
                  <h3>Total Schools</h3>
                  <p className="metric-value">{metrics.totalSchools || 0}</p>
                  <span className="metric-label">Registered in {stateName}</span>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">👥</div>
                <div className="metric-content">
                  <h3>Total Teachers</h3>
                  <p className="metric-value">{metrics.totalTeachers || 0}</p>
                  <span className="metric-label">Across all schools</span>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">🎓</div>
                <div className="metric-content">
                  <h3>Certified Teachers</h3>
                  <p className="metric-value">{metrics.totalCertifiedTeachers || 0}</p>
                  <span className="metric-label">Safety training certified</span>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">✅</div>
                <div className="metric-content">
                  <h3>Compliance Rate</h3>
                  <p className="metric-value">{metrics.overallComplianceRate || 0}%</p>
                  <span className="metric-label">State-wide average</span>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">📜</div>
                <div className="metric-content">
                  <h3>Certificates (Month)</h3>
                  <p className="metric-value">{metrics.certificatesIssuedThisMonth || 0}</p>
                  <span className="metric-label">Issued this month</span>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon">📅</div>
                <div className="metric-content">
                  <h3>Certificates (Year)</h3>
                  <p className="metric-value">{metrics.certificatesIssuedThisYear || 0}</p>
                  <span className="metric-label">Issued this year</span>
                </div>
              </div>

              <div className="metric-card alert-card">
                <div className="metric-icon">⚠️</div>
                <div className="metric-content">
                  <h3>At-Risk Schools</h3>
                  <p className="metric-value">{metrics.atRiskSchools || 0}</p>
                  <span className="metric-label">60-79% compliance</span>
                </div>
              </div>

              <div className="metric-card danger-card">
                <div className="metric-icon">🚨</div>
                <div className="metric-content">
                  <h3>Non-Compliant</h3>
                  <p className="metric-value">{metrics.nonCompliantSchools || 0}</p>
                  <span className="metric-label">Below 60% compliance</span>
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="quick-stats-section">
              <h2>Compliance Distribution</h2>
              <div className="compliance-chart">
                <div className="chart-bar">
                  <div className="bar-label">Compliant (≥80%)</div>
                  <div className="bar-container">
                    <div
                      className="bar-fill bar-compliant"
                      style={{ width: `${(schools.filter(s => s.complianceStatus === 'compliant').length / Math.max(schools.length, 1)) * 100}%` }}
                    >
                      {schools.filter(s => s.complianceStatus === 'compliant').length} schools
                    </div>
                  </div>
                </div>

                <div className="chart-bar">
                  <div className="bar-label">At-Risk (60-79%)</div>
                  <div className="bar-container">
                    <div
                      className="bar-fill bar-at-risk"
                      style={{ width: `${((metrics.atRiskSchools || 0) / Math.max(schools.length, 1)) * 100}%` }}
                    >
                      {metrics.atRiskSchools || 0} schools
                    </div>
                  </div>
                </div>

                <div className="chart-bar">
                  <div className="bar-label">Non-Compliant (&lt;60%)</div>
                  <div className="bar-container">
                    <div
                      className="bar-fill bar-non-compliant"
                      style={{ width: `${((metrics.nonCompliantSchools || 0) / Math.max(schools.length, 1)) * 100}%` }}
                    >
                      {metrics.nonCompliantSchools || 0} schools
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schools Tab */}
        {activeTab === 'schools' && (
          <div className="tab-content">
            {/* Filters and Search */}
            <div className="filters-section">
              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by school name, city, or district..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>

              <div className="filter-controls">
                <div className="filter-group">
                  <label>Filter by Status:</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="filter-select"
                  >
                    <option value="all">All Schools</option>
                    <option value="compliant">Compliant Only</option>
                    <option value="at-risk">At-Risk Only</option>
                    <option value="non-compliant">Non-Compliant Only</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>Sort by:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="filter-select"
                  >
                    <option value="name">School Name</option>
                    <option value="compliance">Compliance %</option>
                    <option value="teachers">Number of Teachers</option>
                    <option value="activity">Last Activity</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Schools Table */}
            <div className="schools-table-container">
              <p className="results-count">Showing {filteredSchools.length} of {schools.length} schools</p>

              <table className="schools-table">
                <thead>
                  <tr>
                    <th>School Name</th>
                    <th>Type</th>
                    <th>City</th>
                    <th>District</th>
                    <th>Teachers</th>
                    <th>Certified</th>
                    <th>Compliance</th>
                    <th>Status</th>
                    <th>Last Activity</th>
                    <th>Contact</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSchools.map((school) => (
                    <tr key={school.schoolId}>
                      <td className="school-name">{school.schoolName || 'N/A'}</td>
                      <td>{school.schoolType || 'N/A'}</td>
                      <td>{school.city || 'N/A'}</td>
                      <td>{school.district || 'N/A'}</td>
                      <td>{school.totalTeachers || 0}</td>
                      <td>{school.certifiedTeachers || 0}</td>
                      <td>
                        <div className="compliance-bar-small">
                          <div
                            className={`compliance-fill ${(school.compliancePercentage || 0) >= 80 ? 'fill-compliant' :
                                    (school.compliancePercentage || 0) >= 60 ? 'fill-at-risk' :
                                        'fill-non-compliant'
                                }`}
                            style={{ width: `${school.compliancePercentage || 0}%` }}
                          />
                          <span className="compliance-text">{school.compliancePercentage || 0}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={getStatusBadgeClass(school.complianceStatus)}>
                          {school.complianceStatus === 'compliant' && '✅ Compliant'}
                          {school.complianceStatus === 'at-risk' && '⚠️ At-Risk'}
                          {school.complianceStatus === 'non-compliant' && '🚨 Non-Compliant'}
                        </span>
                      </td>
                      <td>{formatDate(school.lastActivityDate)}</td>
                      <td>
                        <div className="contact-info">
                          {school.contactEmail && (
                            <a href={`mailto:${school.contactEmail}`} className="contact-link">
                              📧
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredSchools.length === 0 && (
                <div className="no-results">
                  <p>No schools match your search criteria.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Compliance Tab */}
        {activeTab === 'compliance' && metrics && (
          <div className="tab-content">
            <div className="compliance-summary">
              <h2>Compliance Summary</h2>

              <div className="compliance-cards">
                <div className="compliance-card compliant-card">
                  <h3>✅ Compliant Schools</h3>
                  <p className="large-number">
                    {schools.filter(s => s.complianceStatus === 'compliant').length}
                  </p>
                  <p className="card-subtitle">
                    {Math.round((schools.filter(s => s.complianceStatus === 'compliant').length / Math.max(schools.length, 1)) * 100)}% of all schools
                  </p>
                  <p className="card-description">≥80% of teachers certified</p>
                </div>

                <div className="compliance-card at-risk-card">
                  <h3>⚠️ At-Risk Schools</h3>
                  <p className="large-number">{metrics.atRiskSchools || 0}</p>
                  <p className="card-subtitle">
                    {Math.round(((metrics.atRiskSchools || 0) / Math.max(schools.length, 1)) * 100)}% of all schools
                  </p>
                  <p className="card-description">60-79% of teachers certified</p>
                  <p className="card-action">These schools need attention</p>
                </div>

                <div className="compliance-card non-compliant-card">
                  <h3>🚨 Non-Compliant Schools</h3>
                  <p className="large-number">{metrics.nonCompliantSchools || 0}</p>
                  <p className="card-subtitle">
                    {Math.round(((metrics.nonCompliantSchools || 0) / Math.max(schools.length, 1)) * 100)}% of all schools
                  </p>
                  <p className="card-description">&lt;60% of teachers certified</p>
                  <p className="card-action">Immediate action required</p>
                </div>
              </div>

              {/* Schools needing attention */}
              <div className="attention-section">
                <h3>Schools Requiring Attention</h3>
                <div className="attention-list">
                  {schools
                    .filter(s => s.complianceStatus !== 'compliant')
                    .sort((a, b) => (a.compliancePercentage || 0) - (b.compliancePercentage || 0))
                    .slice(0, 10)
                    .map((school) => (
                      <div key={school.schoolId} className="attention-item">
                        <div className="attention-header">
                          <h4>{school.schoolName || 'N/A'}</h4>
                          <span className={getStatusBadgeClass(school.complianceStatus)}>
                            {school.compliancePercentage || 0}%
                          </span>
                        </div>
                        <div className="attention-details">
                          <span>📍 {school.city || 'N/A'}</span>
                          <span>👥 {school.certifiedTeachers || 0}/{school.totalTeachers || 0} certified</span>
                          <span>⏰ {formatDate(school.lastActivityDate)}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && metrics && (
          <div className="tab-content">
            <h2>State-Wide Analytics</h2>

            <div className="analytics-grid">
              <div className="analytics-card">
                <h3>Teacher Certification Progress</h3>
                <div className="progress-stats">
                  <div className="stat-row">
                    <span>Total Teachers:</span>
                    <strong>{metrics.totalTeachers || 0}</strong>
                  </div>
                  <div className="stat-row">
                    <span>Certified:</span>
                    <strong>{metrics.totalCertifiedTeachers || 0}</strong>
                  </div>
                  <div className="stat-row">
                    <span>Remaining:</span>
                    <strong>{(metrics.totalTeachers || 0) - (metrics.totalCertifiedTeachers || 0)}</strong>
                  </div>
                  <div className="progress-bar-large">
                    <div
                      className="progress-fill-large"
                      style={{ width: `${metrics.overallComplianceRate || 0}%` }}
                    />
                  </div>
                  <p className="progress-percentage">{metrics.overallComplianceRate || 0}% Complete</p>
                </div>
              </div>

              <div className="analytics-card">
                <h3>Top Performing Cities</h3>
                <div className="city-rankings">
                  {getCityStats().slice(0, 5).map((city, index) => (
                    <div key={city.name} className="city-rank-item">
                      <span className="rank-number">#{index + 1}</span>
                      <span className="city-name">{city.name}</span>
                      <span className="city-compliance">{city.avgCompliance}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="analytics-card">
                <h3>School Type Distribution</h3>
                <div className="type-distribution">
                  {getSchoolTypeStats().map((type) => (
                    <div key={type.type} className="type-item">
                      <span className="type-name">{type.type}</span>
                      <div className="type-bar">
                        <div
                          className="type-fill"
                          style={{ width: `${(type.count / Math.max(schools.length, 1)) * 100}%` }}
                        />
                        <span className="type-count">{type.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="analytics-card">
                <h3>Certificate Issuance Trends</h3>
                <div className="trend-stats">
                  <div className="trend-item">
                    <span className="trend-label">This Month</span>
                    <span className="trend-value">{metrics.certificatesIssuedThisMonth || 0}</span>
                    <span className="trend-indicator">📈 Recent activity</span>
                  </div>
                  <div className="trend-item">
                    <span className="trend-label">This Year</span>
                    <span className="trend-value">{metrics.certificatesIssuedThisYear || 0}</span>
                    <span className="trend-indicator">📊 Year to date</span>
                  </div>
                  <div className="trend-item">
                    <span className="trend-label">Projected Annual</span>
                    <span className="trend-value">
                      {Math.round((metrics.certificatesIssuedThisMonth || 0) * 12 * 1.15)}
                    </span>
                    <span className="trend-indicator">🎯 Based on current rate</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StateAdminDashboard;

